import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteCustomer } from "@/features/customers/hooks/use-customers";
import type { Customer } from "@/features/customers/types";
import {
  DotsThreeOutlineIcon,
  EyeIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "@/features/utils/i18n";

export const Action = ({ customer }: { customer: Customer }) => {
  const { t } = useTranslation("customers");
  const { company: clientId } = useParams<{ company: string }>();
  const navigate = useNavigate();
  const deleteCustomer = useDeleteCustomer();

  const handleDelete = async () => {
    if (!clientId) return;

    try {
      await deleteCustomer.mutateAsync({
        clientId,
        customerId: customer.id,
      });
      toast.success(t("messages.deleteSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.deleteError"),
      );
    }
  };

  const handleView = () => {
    if (!clientId) return;
    navigate(`/${clientId}/contacts/${customer.id}`);
  };

  const handleEdit = () => {
    if (!clientId) return;
    navigate(`/${clientId}/contacts/${customer.id}/edit`);
  };

  return (
    <div className="text-right flex items-center justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={handleView}>
        <EyeIcon className="h-4 w-4 mr-2" weight="fill" />
        {t("actions.view")}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <DotsThreeOutlineIcon className="h-5 w-5" weight="fill" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleEdit}>
            <PencilSimpleIcon className="h-4 w-4" weight="fill" />
            {t("actions.edit")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="p-0">
            <Button
              variant="ghost"
              onClick={handleDelete}
              className="w-full justify-start text-red-600 hover:text-red-700 focus:text-red-700"
              confirm={t("messages.deleteConfirm")}
            >
              <TrashIcon className="h-4 w-4" weight="fill" />
              {t("actions.delete")}
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
